"use client";

import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useMemo, useState } from "react";
import BenefitCard from "./BenefitCard";
import BlogCard from "./BlogCard";
import BrandCard from "./featured/common/cards/BrandCard";
import CampaignCard from "./CampaignCard";
import CarouselContent from "./CarouselContent";
import PopularFlightTicketCard from "./PopularFlightTicketCard";
import PopularHotelsCard from "./PopularHotelsCard";
import PopularLocationCard from "./PopularLocationCard";
import SectionHeader from "./SectionHeader";
import TabSwitcher from "./TabSwitcher";

const componentMap = {
  CampaignCard,
  BlogCard,
  BrandCard,
  PopularFlightTicketCard,
  PopularLocationCard,
  BenefitCard,
  PopularHotelsCard,
};

// Component type'a göre items filtreleme fonksiyonu
const filterItemsByTab = (items, activeTab, componentType) => {
  if (!items) return [];

  switch (componentType) {
    case "PopularFlightTicketCard":
      return Array.isArray(items) ? filterFlightItems(items, activeTab) : [];
    case "PopularLocationCard":
      return filterLocationItems(items, activeTab);
    default:
      return Array.isArray(items) ? items : [];
  }
};

// Popüler Uçak BİLETİ sectionı için tab'a göre filtrele
const filterFlightItems = (items, activeTab) => {
  if (!items || items.length === 0) return items;

  const domesticFlights =
    items.filter((flight) => flight.IsDomestic === true) || [];
  const internationalFlights =
    items.filter((flight) => flight.IsDomestic === false) || [];

  switch (activeTab) {
    case 1: // Popüler Seferler
      return items;
    case 2: // Popüler İç Hatlar
      return domesticFlights;
    case 3: // Popüler Dış Hatlar
      return internationalFlights;
    default:
      return items;
  }
};

// popüler rotalar sectionı için taba göre filtreleme
const filterLocationItems = (items, activeTab) => {
  if (!items) return [];

  const popularDomesticFlights = items.PopularDomesticFlights || [];
  const popularInternationalFlights = items.PopularInternationalFlights || [];
  const popularCountries = items.PopularCountries || [];

  switch (activeTab) {
    case 1:
      return popularDomesticFlights;
    case 2:
      return popularInternationalFlights;
    case 3:
      return popularCountries;
    default:
      return popularDomesticFlights;
  }
};
export default function CarouselSection({
  title,
  description,
  linkText,
  href,
  preserveTrailingSlash = false,
  items = [],
  componentType,
  componentProps = {},
  showDots = true,
  showDotsFullWidth = false,
  showArrows = false,
  loop = true,
  className = "",
  sectionClassName = "space-y-[21px] pl-4 lg:space-y-6 lg:pl-0",
  showTabs = false,
  tabs: customTabs = [],
  onTabChange,
  carouselProps = {},
}) {
  const t = useTranslationDetector();
  const Component = componentMap[componentType];

  const renderItem = (item, index) => {
    if (!Component) return null;

    // Component'e göre prop mapping
    const props = {
      CampaignCard: { item },
      BlogCard: { post: item },
      BrandCard: { brand: item },
      PopularFlightTicketCard: { item },
      PopularLocationCard: { item },
      BenefitCard: { item },
    };

    const componentProps = props[componentType] || { item };

    return <Component key={item.id ?? index} {...componentProps} />;
  };

  // Component type'a göre default tabs tanımları
  const getDefaultTabsByComponentType = () => {
    return {
      PopularFlightTicketCard: [
        { id: 1, label: t("tabs.popularFlights") },
        { id: 2, label: t("tabs.popularDomestic") },
        { id: 3, label: t("tabs.popularInternational") },
      ],
      PopularLocationCard: [
        { id: 1, label: t("tabs.domesticCities") },
        { id: 2, label: t("tabs.countries") },
        { id: 3, label: t("tabs.popularInternational") },
      ],
    };
  };

  // Tabs'ı hesapla - useMemo ile stabilize et
  const calculatedTabs = useMemo(() => {
    if (customTabs && customTabs.length > 0) {
      return customTabs;
    } else if (showTabs && componentType) {
      const defaultTabsByComponentType = getDefaultTabsByComponentType();
      if (defaultTabsByComponentType[componentType]) {
        return defaultTabsByComponentType[componentType];
      }
    }
    return [];
  }, [componentType, showTabs, customTabs, t]);

  // Active tab'ı hesapla
  const [activeTab, setActiveTab] = useState(() => {
    return calculatedTabs.length > 0 ? calculatedTabs[0].id : 1;
  });
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  // Tab'lı section için items'ı belirle
  const getCurrentItems = () => {
    // Eğer tabItems prop'u varsa ve showTabs aktifse, o tab'ın items'ını kullan
    if (
      showTabs &&
      componentProps.tabItems &&
      componentProps.tabItems[activeTab]
    ) {
      return Array.isArray(componentProps.tabItems[activeTab])
        ? componentProps.tabItems[activeTab]
        : [];
    }

    // Component type'a göre filtreleme yap
    if (showTabs && items) {
      return filterItemsByTab(items, activeTab, componentType);
    }

    return Array.isArray(items) ? items : [];
  };

  const safeItems = getCurrentItems();

  return (
    <div className={sectionClassName}>
      <SectionHeader
        title={title}
        description={description}
        linkText={linkText}
        href={href}
        preserveTrailingSlash={preserveTrailingSlash}
        className="pr-4 lg:pr-0"
      />
      {showTabs && (
        <TabSwitcher
          tabs={calculatedTabs}
          selected={activeTab}
          onChange={handleTabChange}
        />
      )}
      <CarouselContent
        items={safeItems}
        renderItem={renderItem}
        className={className}
        showDots={showDots}
        showDotsFullWidth={showDotsFullWidth}
        showArrows={showArrows}
        loop={loop}
        carouselProps={carouselProps}
      />
    </div>
  );
}
